import React, { FC } from "react";

import {
  PaymentStatuses,
  type Resale,
  type ResaleFragment,
  ResaleStatuses,
  SortDirection,
  useListGroupedResalesQuery,
} from "@api/graphql";
import { GroupedResales } from "@components/grouped_resales/GroupedResales";
import { ResalesAccordionHeader } from "@components/grouped_resales/ResaleAccordionHeader";
import { ProtectedComponent, useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import type { TableHeader } from "@components/table/TableContents";
import type { TableFilterInput } from "@components/table/TableFilter";
import { useAuthenticationContext } from "@context/authentication";
import { ExternalRoles, InternalRoles, Role } from "@roles";
import type { FilterByTyped, SortByTyped } from "@util/index";
import type { ResaleGroup } from "@util/resale";

const PENDING_PAYMENTS_TABLE_HEADERS: TableHeader<ResaleFragment>[] = [
  { label: "ID", accessor: "resale_id" },
  { label: "Artwork title", accessor: "artwork_name" },
  { label: "Artist", accessor: "artist_name" },
  { label: "Sold date", accessor: "date_sold" },
  { label: "Resale price", accessor: "resale_price" },
  { label: "Royalty", accessor: "royalty" },
  { label: "Status", accessor: "payment_status" },
];

const paymentStatusFilterOptions: TableFilterInput[] = [
  {
    option: PaymentStatuses.AMP_INVOICE_REQUESTED,
    defaultValue: true,
  },
  {
    option: PaymentStatuses.HOLD_PERIOD,
    defaultValue: true,
  },
];

export const PendingPayments: FC<{ displayBanner?: boolean; vendorId?: number }> = ({ displayBanner, vendorId }) => {
  const { auth } = useAuthenticationContext();
  const isExternalUser = useIsAuthUserAllowed(ExternalRoles);
  const isInternalUser = useIsAuthUserAllowed(InternalRoles);
  const getAccordionHeader = ({ resales }: ResaleGroup) => {
    return (
      <ProtectedComponent
        requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data, Role.ResaleReporter]}
        auth={auth}
      >
        <ResalesAccordionHeader resales={resales} isExternalUser={isExternalUser} resaleCount={resales.length} />
      </ProtectedComponent>
    );
  };
  const filter_by: FilterByTyped<Resale>[] = [
    {
      field: "resale_status",
      in: [ResaleStatuses.CONFIRMED_ELIGIBLE, ResaleStatuses.RESEARCH_REQUIRED],
    },
    {
      field: "batch_group_confirmed",
      eq: "true",
    },
  ];

  const sort_by: SortByTyped<Resale> = {
    field: "reported_on",
    direction: SortDirection.DESCENDING,
  };

  if (isInternalUser && vendorId) {
    filter_by.push({ field: "vendor_id", eq: vendorId.toString() });
  }

  return (
    <GroupedResales
      queryFunction={useListGroupedResalesQuery}
      getQueryVariables={(page) => ({
        input: { page, limit: 10, filter_by, sort_by },
      })}
      updateQueryVariablesWithFilter={({ input: { page, limit, filter_by, sort_by } }, new_filter_by) => ({
        input: {
          page,
          limit,
          filter_by: filter_by.concat(new_filter_by ? new_filter_by : []),
          sort_by,
        },
      })}
      getResaleGroupsFromQueryData={(data) => data.listGroupedResales.results}
      getPaginatedOutputFromQueryData={(data) => data.listGroupedResales}
      getAccordionHeader={getAccordionHeader}
      sourcePage={"PendingPayments"}
      customTableHeaders={PENDING_PAYMENTS_TABLE_HEADERS}
      paymentStatusFilterOptions={paymentStatusFilterOptions}
      displayBanner={displayBanner}
    />
  );
};
